// Per-city header copy and search metadata.
// Keeps every city page distinct for crawlers instead of shipping one
// templated title/description across all slugs.

export type CitySeoCopy = {
  /** Short eyebrow shown above the hero heading. */
  eyebrow: string;
  /** Visible hero sub-headline (falls back to the DB tagline when absent). */
  intro: string;
  title: string;
  description: string;
  keywords: string;
};

const titleCase = (slug: string) =>
  slug
    .split("-")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

const CITY_COPY: Record<string, CitySeoCopy> = {
  helsinki: {
    eyebrow: "Capital region · Uusimaa",
    intro:
      "Design district cafés, seafront saunas, harbour markets and a nightlife scene that runs from natural-wine bars to island clubs — curated by locals, refreshed every week.",
    title: "Helsinki Guide: Best Restaurants, Saunas & Events | what Finland",
    description:
      "Local guide to Helsinki: editor-picked restaurants and cafés, seaside saunas, design-district walks, island trips and this week's events, with tickets and maps.",
    keywords:
      "Helsinki guide, best restaurants Helsinki, Helsinki saunas, things to do in Helsinki, Helsinki events this week, Helsinki design district",
  },
  turku: {
    eyebrow: "Southwest Finland · Archipelago gateway",
    intro:
      "Finland's oldest city: riverside terraces, a medieval castle, market-hall food and the ferry gateway to thousands of archipelago islands.",
    title: "Turku Guide: Riverside Dining, Archipelago & Events | what Finland",
    description:
      "Plan Turku, Finland: riverside restaurants and market-hall food, castle and cathedral visits, archipelago day trips and current events with dates and tickets.",
    keywords:
      "Turku guide, restaurants Turku, Turku archipelago, things to do in Turku, Turku events, Turku castle",
  },
  tampere: {
    eyebrow: "Pirkanmaa · Sauna capital",
    intro:
      "The sauna capital of the world: public wood-heated saunas between two lakes, red-brick industrial halls turned food markets, and a live-music calendar all year.",
    title: "Tampere Guide: Public Saunas, Food Halls & Events | what Finland",
    description:
      "Local guide to Tampere: the world's public sauna capital, lakeside swims, Finlayson food halls, best restaurants and upcoming concerts, festivals and matches.",
    keywords:
      "Tampere guide, Tampere saunas, restaurants Tampere, things to do in Tampere, Tampere events, Finlayson",
  },
  hanko: {
    eyebrow: "Uusimaa · Southernmost town",
    intro:
      "Finland's southern tip: wooden seaside villas, thirty kilometres of beach, regatta summers and long harbour dinners with the sun still up at midnight.",
    title: "Hanko Guide: Beaches, Seaside Dining & Regatta | what Finland",
    description:
      "Guide to Hanko, Finland's southernmost town: sandy beaches, historic villas, harbour restaurants, summer regatta dates and things to do all season.",
    keywords:
      "Hanko guide, Hanko beaches, restaurants Hanko, things to do in Hanko, Hanko regatta, Hanko summer",
  },
  porvoo: {
    eyebrow: "Uusimaa · Medieval old town",
    intro:
      "Cobbled lanes, the red shore houses on the river, chocolate makers and cafés — an easy day trip from Helsinki that rewards a slow afternoon.",
    title: "Porvoo Guide: Old Town, Riverside Cafés & Day Trips | what Finland",
    description:
      "Visit Porvoo: the medieval old town and red shore houses, riverside cafés and chocolate shops, plus events and how to day-trip from Helsinki.",
    keywords:
      "Porvoo guide, Porvoo old town, Porvoo cafes, things to do in Porvoo, Porvoo day trip from Helsinki",
  },
};

export function getCitySeoCopy(slug: string, cityName?: string | null): CitySeoCopy {
  const key = (slug || "").toLowerCase();
  const known = CITY_COPY[key];
  if (known) return known;

  const name = cityName?.trim() || titleCase(key);
  return {
    eyebrow: "Finland",
    intro: `Where to eat, what to do and what's on in ${name} — a local shortlist of restaurants, experiences, saunas and events, kept current by our editors.`,
    title: `${name} Guide: Restaurants, Things To Do & Events | what Finland`,
    description: `Local guide to ${name}, Finland: handpicked restaurants and cafés, things to do and saunas, plus upcoming events with dates, venues and tickets.`,
    keywords: `${name} guide, restaurants ${name}, things to do in ${name}, ${name} events, ${name} Finland travel`,
  };
}
